/**
 * حمايةُ الجلسة في المتصفّح (23 سبتمبر 2026 — نصفُها الآخر في الخادم، الترحيل 0054).
 *
 * · **النشاط** نقرةٌ أو كتابةٌ أو لمسٌ أو تمرير — أو فيديو يعمل (بثُّ الفصل يُشاهَد بلا نقر).
 *   يُحفظ في `localStorage` فيراه كلُّ تبويب: من يعمل في تبويبٍ لا تُغلَق جلستُه في آخر.
 * · عند كل تدوير يُرسَل `idleSeconds` (كم مضى على آخر نشاط)، والخادمُ هو من يحكم:
 *   تجاوُز الخمول أو الحدّ المطلق يُرفض ويُنهي الجلسة. والمتصفّحُ ينبّه قبل ذلك بدقيقتين.
 * · سببُ الخروج يُحفظ في تخزين التبويب فتقوله صفحةُ الدخول بجملة — لا خروجٌ صامت.
 */
import type { SessionInfo } from './types';

const ACT_KEY = 'rafrafa.lastActivity';
const REMEMBER_KEY = 'rafrafa.rememberDevice';
const SESSION_KEY = 'rafrafa.session';
const REASON_KEY = 'rafrafa.signoutReason';
/** كتابةٌ واحدة كل خمس ثوانٍ تكفي — لا حاجة لدقّةٍ أعلى من ذلك في قياسٍ بالدقائق */
const THROTTLE_MS = 5_000;

/**
 * آخرُ نشاطٍ عند تحميل الصفحة يُقرأ من التخزين لا يُفترض «الآن»: إعادةُ تحميلٍ على جهازٍ متروكٍ ساعتين
 * ليست نشاطاً — وإلا أحيت الجلسةَ لمن وجد الجهاز. ومن لا قيمةَ محفوظةً له (أول تحميلٍ بعد التحديث) يبدأ الآن.
 */
function initialLast(): number {
  try {
    const v = Number(localStorage.getItem(ACT_KEY));
    if (v > 0) return v;
    const now = Date.now();
    localStorage.setItem(ACT_KEY, String(now));
    return now;
  } catch { return Date.now(); }
}
let last = initialLast();

export function markActive(force = false): void {
  const now = Date.now();
  if (!force && now - last < THROTTLE_MS) return;
  last = now;
  try { localStorage.setItem(ACT_KEY, String(now)); } catch { /* تخزين معطّل: يبقى القياسُ في هذا التبويب */ }
}

export function lastActivity(): number {
  let stored = 0;
  try { stored = Number(localStorage.getItem(ACT_KEY)) || 0; } catch { /* تجاهل */ }
  return Math.max(last, stored);
}

export function idleSeconds(): number {
  return Math.max(0, Math.floor((Date.now() - lastActivity()) / 1000));
}

/** فيديو يعمل = مشاهدةٌ حاضرة (بثّ الفصل، تسجيل جلسة) — لا خمول */
export function mediaPlaying(): boolean {
  return Array.from(document.querySelectorAll('video, audio')).some((m) => {
    const el = m as HTMLMediaElement;
    return !el.paused && !el.ended;
  });
}

let installed = false;
export function installActivityTracking(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const on = () => markActive();
  for (const ev of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll']) {
    window.addEventListener(ev, on, { passive: true, capture: true });
  }
}

/**
 * «ابقَ مسجّلاً على هذا الجهاز» (0068): اختيارُ صاحب الجهاز يُحفظ فيه — يعلّمه مرّةً ويجده معلَّماً في كل
 * دخولٍ تالٍ على الجهاز نفسِه. القيمةُ تفضيلٌ للخانة لا سرّ؛ والخادمُ هو من يقرّر إن كان للحساب جلسةٌ موثوقة.
 *
 * **الافتراضُ معلَّم** (25 أكتوبر 2026 — «الإشعارات ما توصل لأن كل شوي يطلب تسجيل دخول»): من لم يختر شيئاً
 * يبقى داخلاً على جهازه؛ ومن أزال العلامة مرّةً تبقى مزالةً على ذلك الجهاز (`'0'`). وإدارةُ المنصة جلستُها الموثوقة
 * أقصر (سبعة أيام خمولاً — 26 أكتوبر 2026)، والجهةُ الرقابية لا يوثَّق جهازُها مهما كانت الخانة — الخادمُ يحكم.
 */
export const rememberDevice = {
  get(): boolean {
    try { return localStorage.getItem(REMEMBER_KEY) !== '0'; } catch { return true; }
  },
  set(v: boolean) {
    try { localStorage.setItem(REMEMBER_KEY, v ? '1' : '0'); } catch { /* تجاهل */ }
  },
};

const DEVICES_KEY = 'rafrafa.mfaDevices';
type DeviceMap = Record<string, { token: string; until: string }>;
const readDevices = (): DeviceMap => { try { const raw = localStorage.getItem(DEVICES_KEY); return raw ? (JSON.parse(raw) as DeviceMap) : {}; } catch { return {}; } };
const writeDevices = (m: DeviceMap) => { try { if (Object.keys(m).length) localStorage.setItem(DEVICES_KEY, JSON.stringify(m)); else localStorage.removeItem(DEVICES_KEY); } catch { /* تخزينٌ محجوب: يُطلب الرمزُ كل مرّة لا غير */ } };

/**
 * الجهازُ الموثوق للتحقّق بخطوتين (0089 — «أحطّ احفظ هذا الجهاز ويطلبني مرّةً ثانية»): بعد رمز التطبيق مع الخانة يعيد الخادمُ رمزاً
 * يُحفظ هنا **بالبريد** (جهازٌ يدخل منه أكثرُ من حساب)، ويُرسل مع كلمة المرور فيُتخطّى رمزُ التطبيق حتى انتهائه. **لا يُمسح عند الخروج**
 * (`tokens.clear`) — الخروجُ يُنهي الجلسة لا ثقةَ الجهاز؛ والخادمُ يُسقطه بتغيير كلمة المرور أو إيقاف التحقّق أو «أنهِ جلساتي الأخرى».
 */
export const trustedDevice = {
  get(email: string): string | undefined {
    const d = readDevices()[email.trim().toLowerCase()];
    return d && Date.parse(d.until) > Date.now() ? d.token : undefined;
  },
  set(email: string, token: string, until: string) {
    const m = readDevices();
    for (const [k, v] of Object.entries(m)) if (Date.parse(v.until) <= Date.now()) delete m[k];
    m[email.trim().toLowerCase()] = { token, until };
    writeDevices(m);
  },
};

export const sessionInfo = {
  get(): SessionInfo | null {
    try { const raw = localStorage.getItem(SESSION_KEY); return raw ? (JSON.parse(raw) as SessionInfo) : null; } catch { return null; }
  },
  set(s: SessionInfo | undefined) {
    if (!s) return;
    try { localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch { /* تجاهل */ }
  },
  clear() {
    try { localStorage.removeItem(SESSION_KEY); } catch { /* تجاهل */ }
  },
};

/** سببُ الخروج — تقوله صفحةُ الدخول مرةً ثم يُمحى */
export const signoutReason = {
  set(text: string) {
    try { sessionStorage.setItem(REASON_KEY, text); } catch { /* تجاهل */ }
  },
  take(): string | null {
    try { const v = sessionStorage.getItem(REASON_KEY); sessionStorage.removeItem(REASON_KEY); return v; } catch { return null; }
  },
  peek(): string | null {
    try { return sessionStorage.getItem(REASON_KEY); } catch { return null; }
  },
};

/** رموزُ الخادم التي تعني «انتهت جلستك» وجملتُها لصفحة الدخول */
export const SESSION_END_TEXT: Record<string, string> = {
  SESSION_IDLE: 'انتهت جلستك لعدم النشاط حمايةً لبيانات الحساب — ادخل من جديد وتابع من حيث توقفت.',
  SESSION_EXPIRED: 'انتهت المدّة القصوى للجلسة حمايةً للحساب — ادخل من جديد.',
  SESSION_ENDED: 'أُنهيت هذه الجلسة (من «جلساتي» أو من الإدارة أو بالخروج من تبويبٍ آخر) — ادخل من جديد.',
  TOKEN_REUSED: 'أُوقفت الجلسة احتياطاً لأن رمزها استُعمل مرتين — ادخل من جديد، وإن تكرّر فغيّر كلمة المرور.',
};
